/**
 * Validates Colombian phone numbers
 * Supports:
 * - Mobile: 10 digits starting with 3 (e.g., 3001234567)
 * - Landline: 10 digits starting with 1, 2, 4, 5, 6, 7, 8, 9 (e.g., 6011234567)
 * - International format: +57 followed by 10 digits without the initial 0
 * @param phone - The phone number to validate (can be string, number, null, or undefined)
 * @returns true if valid Colombian phone number, false otherwise
 */
export const validatePhone = (
  phone: string | number | null | undefined
): boolean => {
  // Handle null or undefined values
  if (phone === null || phone === undefined) {
    return false;
  }

  // Convert to string and remove spaces, hyphens, parentheses
  const cleanedPhone = String(phone)
    .trim()
    .replace(/[\s\-()]/g, "");

  // Pattern for Colombian phone numbers
  // Mobile: 3 followed by 9 digits (10 total)
  // Landline: 1-9 (except 3) followed by 9 digits (10 total)
  // International: +57 followed by 10 digits
  const colombianPhonePattern = /^(\+57)?([1-9]\d{9})$/;

  return colombianPhonePattern.test(cleanedPhone);
};

/**
 * Formats a phone number to Colombian standard format
 * Input: 3001234567 or +573001234567
 * Output: +57 300 123 4567
 * @param phone - The phone number to format
 * @returns Formatted phone string or empty string if invalid
 */
export const formatPhone = (
  phone: string | number | null | undefined
): string => {
  if (!validatePhone(phone)) {
    return "";
  }

  // Clean the phone number
  const cleanedPhone = String(phone)
    .trim()
    .replace(/[\s\-()]/g, "");

  // Remove +57 prefix if present
  const phoneDigits = cleanedPhone.replace(/^\+57/, "");

  // Format as +57 XXX XXX XXXX
  return `+57 ${phoneDigits.slice(0, 3)} ${phoneDigits.slice(3, 6)} ${phoneDigits.slice(
    6
  )}`;
};

/**
 * Extracts just the digits from a phone number
 * Input: "+57 300 123 4567" or "3001234567"
 * Output: "3001234567"
 * @param phone - The phone number to extract digits from
 * @returns String with only digits or empty string if invalid
 */
export const extractPhoneDigits = (
  phone: string | number | null | undefined
): string => {
  if (!validatePhone(phone)) {
    return "";
  }

  const cleanedPhone = String(phone)
    .trim()
    .replace(/[\s\-()]/g, "");

  return cleanedPhone.replace(/^\+57/, "");
};
